import { useEffect } from "react";
import { Reveal, Eyebrow, PageHero, PrimaryLink, ArrowLink } from "@/components/shared";
import { IMAGES } from "@/data/content";
import { useLang } from "@/i18n";

const About = ({ scrollTo }) => {
  const { t } = useLang();

  useEffect(() => {
    if (scrollTo) {
      const timer = setTimeout(() => {
        document.getElementById(scrollTo)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [scrollTo]);

  const visionPillars = [
    ["about.vision1Title", "about.vision1Body"],
    ["about.vision2Title", "about.vision2Body"],
    ["about.vision3Title", "about.vision3Body"],
  ];

  return (
    <>
      <PageHero
        testId="about-hero"
        eyebrow={t("about.heroEyebrow")}
        title={t("about.heroTitle")}
        intro={t("about.heroIntro")}
      />

      <section className="bg-ivory" id="about-ibmct" data-testid="about-story-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>{t("about.storyEyebrow")}</Eyebrow>
            <h2 className="text-[32px] sm:text-4xl font-semibold text-charcoal">{t("about.storyHeading")}</h2>
            <p className="mt-3 font-sans font-semibold text-saffrondeep" data-testid="about-founders-line">
              {t("about.storyFounders")}
            </p>
            <p className="mt-6 text-charcoal/80 leading-relaxed">{t("about.storyP1")}</p>
            <p className="mt-4 text-charcoal/80 leading-relaxed">{t("about.storyP2")}</p>
            <p className="mt-4 text-charcoal/80 leading-relaxed">{t("about.storyP3")}</p>
            <p className="mt-4 text-charcoal/80 leading-relaxed">{t("about.storyP4")}</p>
            <p className="mt-4 text-charcoal/80 leading-relaxed">{t("about.storyP5")}</p>
          </Reveal>
          <Reveal delay={100}>
            <img src={IMAGES.praying} alt="IBMCT monks in prayer during a community gathering" className="rounded-lg w-full object-cover aspect-[4/3]" data-testid="about-story-image" />
          </Reveal>
        </div>
      </section>

      <section className="bg-cream" id="spiritual-guidance" data-testid="about-spiritual-guidance-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-4">
              <img
                src={IMAGES.portrait}
                alt="Portrait of Venerable Bhante Bodhidatta Thera, General Secretary of IBMCT"
                className="rounded-full w-64 h-64 lg:w-80 lg:h-80 object-cover mx-auto border-4 border-sand"
                data-testid="about-leadership-portrait"
              />
            </Reveal>
            <Reveal className="lg:col-span-8" delay={100}>
              <Eyebrow>Spiritual Guidance</Eyebrow>
              <p className="font-serif italic text-lg text-charcoal/70 mb-3">Rooted in the wisdom of the Dhamma.</p>
              <h2 className="text-[32px] sm:text-4xl font-semibold text-charcoal">Venerable Bhante Bodhidatta Thera</h2>
              <p className="mt-2 font-sans font-semibold text-saffrondeep">General Secretary, International Buddhist Monks Charitable Trust</p>
              <blockquote className="mt-6 font-serif italic text-2xl lg:text-[28px] text-charcoal/90 border-l-2 border-saffron pl-6" data-testid="about-leadership-quote">
                “The path of Dhamma is not walked alone — it is walked for all beings.”
              </blockquote>
              <p className="mt-6 text-charcoal/80 leading-relaxed max-w-3xl">
                Born as Jagadish on 3 May 1972 in Bengaluru district, Karnataka, Venerable Bhante Bodhidatta Thera completed a B.E. in
                Civil Engineering before dedicating his life to the Dhamma and to those at the margins of society. He received full
                monastic ordination at Bodh Gaya in 2007, and has since led IBMCT’s work in community service, education, healthcare
                and international Dhamma outreach.
              </p>
              <ul className="mt-6 flex flex-wrap gap-3" data-testid="about-leadership-at-a-glance">
                {["B.E. Civil Engineering", "Ordained 2007, Bodh Gaya", "30,000+ KM Padayatra", "International Dhamma Outreach"].map((g) => (
                  <li key={g} className="font-sans text-[13px] font-semibold text-charcoal border border-[rgba(41,39,34,0.16)] rounded-full px-4 py-2 bg-ivory">
                    {g}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <ArrowLink to="/leadership" testId="about-read-journey-button">Read His Journey</ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ivory" id="nalanda" data-testid="about-nalanda-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <div className="img-zoom !rounded-2xl aspect-[4/3]">
                <img src={IMAGES.outdoor} alt="Buddhist monks in prayer outdoors at an IBMCT gathering" className="w-full h-full object-cover" loading="lazy" data-testid="about-nalanda-image" />
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="thin-sep w-14 mb-6" aria-hidden="true" />
              <Eyebrow>Nalanda Study Centre</Eyebrow>
              <h2 className="text-[32px] sm:text-4xl font-semibold text-charcoal">Reviving the Spirit of Nalanda</h2>
              <p className="mt-5 text-charcoal/80 leading-relaxed">
                A living centre for Dhamma, meditation, scholarship and cultural preservation — completed in 2024 in Karnataka.
              </p>
              <div className="mt-8" data-testid="about-nalanda-status">
                <span className="inline-flex items-center gap-2 rounded-full bg-saffron/15 border border-saffron/40 px-5 py-2.5 font-sans text-sm font-semibold text-saffrondeep" data-testid="about-nalanda-completed-badge">
                  <span className="w-2 h-2 rounded-full bg-saffron" aria-hidden="true" />
                  Completed in 2024
                </span>
              </div>
              <ol className="mt-8 space-y-3" data-testid="about-nalanda-timeline">
                {[
                  ["2017", "Land Purchased"],
                  ["2021", "Construction Began"],
                  ["2024", "Completed"],
                ].map(([y, l]) => (
                  <li key={y} className="flex items-baseline gap-4">
                    <span className="font-serif text-xl font-semibold text-saffrondeep w-14 shrink-0">{y}</span>
                    <span className="font-sans text-[15px] font-semibold text-charcoal">{l}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-9 flex flex-wrap gap-4">
                <PrimaryLink to="/nalanda" testId="about-nalanda-explore-button">Explore Nalanda</PrimaryLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-coal text-white" id="vision" data-testid="about-vision-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <Reveal>
            <Eyebrow dark>{t("about.visionEyebrow")}</Eyebrow>
            <h2 className="text-[32px] sm:text-4xl lg:text-5xl font-semibold text-ivory max-w-3xl">
              {t("about.visionHeading")}
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
              {visionPillars.map(([titleKey, bodyKey], i) => (
                <article key={titleKey} className="border border-[rgba(245,239,228,0.25)] rounded-lg p-7" data-testid={`vision-pillar-${i}`}>
                  <span className="font-serif text-[15px] font-semibold text-saffron-light/80" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-2 font-serif text-xl font-semibold text-ivory">{t(titleKey)}</h3>
                  <p className="mt-3 text-[15px] text-[rgba(245,239,228,0.8)] leading-relaxed">{t(bodyKey)}</p>
                </article>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default About;
